import { useCallback, useMemo, useState } from 'react'
import type { FiltersState } from '~/components/Filters/FilterBar/types'
import type {
  DataTableColumnFilter,
  DataTableQueryParams,
  DataTableSort
} from './types'

const COLUMN_FILTER_PREFIX = 'c.'

type SearchRecord = Record<string, unknown>

export const encodeDataTableSearch = (
  query: Pick<DataTableQueryParams, 'page' | 'sorting' | 'columnFilters'>,
  defaults?: { sorting?: DataTableSort[] }
): Record<string, string> => {
  const out: Record<string, string> = {}
  if (query.page > 1) {
    out.page = String(query.page)
  }

  const sort = query.sorting[0]
  const defaultSort = defaults?.sorting?.[0]
  const encodeSort = (s: DataTableSort): string => (s.desc ? `-${s.id}` : s.id)

  if (sort) {
    const encoded = encodeSort(sort)
    const defaultEncoded = defaultSort ? encodeSort(defaultSort) : undefined
    if (encoded !== defaultEncoded) {
      out.sort = encoded
    }
  }

  for (const filter of query.columnFilters) {
    if (
      filter.value === undefined ||
      filter.value === null ||
      filter.value === ''
    ) {
      continue
    }
    out[`${COLUMN_FILTER_PREFIX}${filter.id}`] = String(filter.value)
  }

  return out
}

export const parseDataTableSearch = (
  search: SearchRecord,
  defaults: { sorting: DataTableSort[]; itemsPerPage: number }
): {
  tableQuery: DataTableQueryParams
  panelFilters: FiltersState
} => {
  const panelFilters: FiltersState = {}
  const columnFilters: DataTableColumnFilter[] = []
  let page = 1
  let sorting = defaults.sorting

  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null || value === '') continue
    const str = String(value)

    if (key === 'page') {
      if (/^\d+$/.test(str)) {
        const n = Number(str)
        if (n >= 1) page = n
      }
      continue
    }

    if (key === 'sort') {
      if (str.startsWith('-')) {
        sorting = [{ id: str.slice(1), desc: true }]
      } else {
        sorting = [{ id: str, desc: false }]
      }
      continue
    }

    if (key.startsWith(COLUMN_FILTER_PREFIX)) {
      columnFilters.push({
        id: key.slice(COLUMN_FILTER_PREFIX.length),
        value: str
      })
      continue
    }

    panelFilters[key] = str
  }

  return {
    tableQuery: {
      page,
      itemsPerPage: defaults.itemsPerPage,
      sorting,
      columnFilters
    },
    panelFilters
  }
}

type NavigateSearch = (opts: {
  search: Record<string, string>
  replace?: boolean
}) => void

type UseDataTableSearchParamsOptions = {
  search: SearchRecord
  navigate: NavigateSearch
  defaultSorting: DataTableSort[]
  defaultPageSize?: number
}

export const useDataTableSearchParams = ({
  search,
  navigate,
  defaultSorting,
  defaultPageSize = 20
}: UseDataTableSearchParamsOptions) => {
  const [itemsPerPage, setItemsPerPage] = useState(defaultPageSize)

  const { tableQuery, panelFilters } = useMemo(
    () =>
      parseDataTableSearch(search, {
        sorting: defaultSorting,
        itemsPerPage
      }),
    [search, defaultSorting, itemsPerPage]
  )

  const queryParams = useMemo(
    (): DataTableQueryParams => ({
      ...tableQuery,
      itemsPerPage
    }),
    [tableQuery, itemsPerPage]
  )

  const setQueryParams = useCallback(
    (next: DataTableQueryParams) => {
      if (next.itemsPerPage !== itemsPerPage) {
        setItemsPerPage(next.itemsPerPage)
      }
      const encoded = encodeDataTableSearch(next, { sorting: defaultSorting })
      navigate({
        search: { ...panelFilters, ...encoded }
      })
    },
    [defaultSorting, itemsPerPage, navigate, panelFilters]
  )

  const setPanelFilters = useCallback(
    (filters: FiltersState, areDefault = false) => {
      const clean = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== '')
      )
      const encoded = encodeDataTableSearch(
        {
          page: 1,
          sorting: queryParams.sorting,
          columnFilters: queryParams.columnFilters
        },
        { sorting: defaultSorting }
      )
      navigate({
        search: { ...clean, ...encoded },
        replace: areDefault
      })
    },
    [defaultSorting, navigate, queryParams.columnFilters, queryParams.sorting]
  )

  const resetAllFilters = useCallback(() => {
    const encoded = encodeDataTableSearch(
      {
        page: 1,
        sorting: queryParams.sorting,
        columnFilters: []
      },
      { sorting: defaultSorting }
    )
    navigate({
      search: encoded
    })
  }, [defaultSorting, navigate, queryParams.sorting])

  return {
    queryParams,
    setQueryParams,
    panelFilters,
    setPanelFilters,
    resetAllFilters
  }
}
